import { useEffect, useState, startTransition } from 'react'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { fetchCinematics } from '@/services/fetch-cinematics'
import { ICinematics } from '@/types'
import { isEmpty } from '@/utils/nunchee-kit'
import { useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { mergeCinematics } from '@/cinematics/utils/mergeCinematics'
import { clarity, CLARITY_TAGS } from '@/utils/clarity'

export function useBranding({
  brandingTag = '',
  enabled = true,
  revertToOriginal = false
}: {
  brandingTag?: string,
  enabled?: boolean,
  revertToOriginal?: boolean
}) {
  const { resetCinematics, originalCinematics: storedOriginal, isUsingLocalCinematics } = useCinematicsStore()
  const [isApplied, setIsApplied] = useState(false)

  // When the eligible tag changes to a different non-empty value mid-session
  // (e.g. after a branding-eligible purchase), reset so the new tag is fetched and
  // merged over originalCinematics. Adjust-state-during-render keeps this off the
  // effect path (react-compiler safe) — no re-merge stacking since the merge base
  // is always storedOriginal.
  const [appliedTag, setAppliedTag] = useState(brandingTag)
  if (brandingTag !== appliedTag) {
    setAppliedTag(brandingTag)
    if (brandingTag && !revertToOriginal) {
      setIsApplied(false)
    }
  }

  const { data: brandingCinematics, isError } = useQuery({
    queryKey: QUERY_KEYS.cinematics(brandingTag),
    queryFn: () => fetchCinematics({ tag: brandingTag }),
    enabled: enabled && !isApplied && !!brandingTag && !revertToOriginal && !isUsingLocalCinematics,
    retry: 2,
  })

  // Apply branding when both the store's original cinematics and the branding data are available
  useEffect(() => {
    if (revertToOriginal) return

    if (storedOriginal && !isEmpty(storedOriginal) && brandingCinematics && !isEmpty(brandingCinematics) && !isApplied) {
      resetCinematics(mergeCinematics({
        base: storedOriginal,
        override: brandingCinematics as ICinematics
      }))
      clarity.set(CLARITY_TAGS.BRANDING_TAG, brandingTag)
      startTransition(() => setIsApplied(true))
    }
  }, [storedOriginal, brandingCinematics, resetCinematics, isApplied, revertToOriginal, brandingTag])

  // Give up gracefully after all retries fail so the overlay doesn't stay stuck
  useEffect(() => {
    if (isError && !isApplied) {
      startTransition(() => setIsApplied(true))
    }
  }, [isError, isApplied])

  // When using a local cinematic override, never fetch/merge branding —
  // mark as applied so the loading overlay can clear.
  useEffect(() => {
    if (isUsingLocalCinematics && !isApplied) {
      startTransition(() => setIsApplied(true))
    }
  }, [isUsingLocalCinematics, isApplied])

  // Restore original cinematics on logout
  useEffect(() => {
    if (revertToOriginal && storedOriginal && !isEmpty(storedOriginal)) {
      resetCinematics(storedOriginal)
      startTransition(() => setIsApplied(false))
    }
  }, [revertToOriginal, storedOriginal, resetCinematics])

  return {
    isApplied,
    // Loading until branding is fully applied (not just until data arrives).
    // This prevents the overlay from hiding before storedOriginal is ready or before the effect has run.
    isLoading: !revertToOriginal && enabled && !isApplied
  }
}
